import { useState, useEffect, useCallback } from "react";
import fuzzy from "fuzzy";

import { deburr } from "../../deburr";
import { searchIcon } from "../icons";

import { useLibraryCache } from "../../hooks/useLibraryItemSvg";
import { useApp, useDevice } from "../App";

import {
  premiumLibraryCategories,
  type PremiumLibraryItem,
} from "../../data/premiumLibraryData";
import { distributeLibraryItemsOnSquareGrid } from "../../data/library";
import { getPremiumStatus } from "../../data/premiumAuth";

import { PremiumLibraryUnit } from "./PremiumLibraryUnit";
import { PurchasePrompt } from "./PurchasePrompt";
import "./PremiumLibraryMenu.scss";

import type { LibraryItems } from "../../types";

interface PremiumLibraryMenuProps {
  onClose?: () => void;
  searchQuery?: string;
}

export const PremiumLibraryMenu = ({
  onClose,
  searchQuery = "",
}: PremiumLibraryMenuProps) => {
  const app = useApp();
  const { onInsertElements } = app;
  const device = useDevice();

  // Use proper device detection instead of matchMedia
  const isMobile = device.editor.isMobile;

  const [showPurchasePrompt, setShowPurchasePrompt] = useState(false);
  const [restrictedItem, setRestrictedItem] =
    useState<PremiumLibraryItem | null>(null);
  const [selectedItems, setSelectedItems] = useState<
    PremiumLibraryItem["id"][]
  >([]);

  const premiumStatus = getPremiumStatus();
  const isPremium = premiumStatus.hasPremium;

  // Get all items
  const getAllItems = useCallback((): PremiumLibraryItem[] => {
    return premiumLibraryCategories.flatMap((category) => category.items);
  }, []);

  // Filter items based on search query
  const getFilteredItems = (): PremiumLibraryItem[] => {
    const allItems = getAllItems();

    if (!searchQuery.trim()) {
      return allItems;
    }

    const query = deburr(searchQuery.toLowerCase().replace(/[<>_| -]/g, ""));

    return fuzzy
      .filter(query, allItems, {
        extract: (item) =>
          `${deburr((item.name || "").toLowerCase())} ${
            item.tags?.join(" ") || ""
          }`,
      })
      .sort((a, b) => b.score - a.score)
      .map((result) => result.original);
  };

  // Group filtered items by category for display
  const groupItemsByCategory = (
    items: PremiumLibraryItem[],
  ): Record<string, PremiumLibraryItem[]> => {
    const grouped: Record<string, PremiumLibraryItem[]> = {};

    items.forEach((item) => {
      const category = premiumLibraryCategories.find((cat) =>
        cat.items.some((catItem) => catItem.id === item.id),
      );

      if (category) {
        const categoryName = category.name;
        if (!grouped[categoryName]) {
          grouped[categoryName] = [];
        }
        grouped[categoryName].push(item);
      }
    });

    return grouped;
  };

  const filteredItems = getFilteredItems();
  const itemsByCategory = groupItemsByCategory(
    filteredItems,
  );

  const libraryCache = useLibraryCache();
  const svgCache = libraryCache.svgCache;

  const deselectItems = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const onInsertLibraryItems = useCallback(
    (libraryItems: LibraryItems) => {
      onInsertElements(distributeLibraryItemsOnSquareGrid(libraryItems));
    },
    [onInsertElements],
  );

  const handleItemClick = useCallback(
    (id: PremiumLibraryItem["id"] | null) => {
      if (!id) {
        return;
      }

      const allItems = getAllItems();
      const item = allItems.find((item) => item.id === id);
      if (!item) {
        return;
      }

      if (item.tier === "premium" && !isPremium) {
        setRestrictedItem(item);
        setShowPurchasePrompt(true);
        return;
      }

      // Convert to LibraryItems format
      const libraryItems: LibraryItems = [item];
      onInsertLibraryItems(libraryItems);
      deselectItems();

      // Close panel on mobile after inserting item
      if (isMobile && onClose) {
        onClose();
      }
    },
    [
      getAllItems,
      isPremium,
      onInsertLibraryItems,
      deselectItems,
      isMobile,
      onClose,
    ],
  );

  const handleItemSelectToggle = useCallback(
    (id: PremiumLibraryItem["id"], event: React.MouseEvent) => {
      setSelectedItems((prev) => {
        if (prev.includes(id)) {
          return prev.filter((itemId) => itemId !== id);
        }
        return [...prev, id];
      });
    },
    [],
  );

  const handleItemDrag = useCallback(
    (id: PremiumLibraryItem["id"], event: React.DragEvent) => {
      // Handle drag logic here if needed
      // eslint-disable-next-line no-console
      console.log("Dragging item:", id);
    },
    [],
  );

  const isItemSelected = useCallback(
    (id: PremiumLibraryItem["id"] | null) => {
      return id ? selectedItems.includes(id) : false;
    },
    [selectedItems],
  );

  const handlePremiumPrompt = useCallback((item: PremiumLibraryItem) => {
    setRestrictedItem(item);
    setShowPurchasePrompt(true);
  }, []);

  return (
    <div className="premium-library-menu">
      {/* Commands/Items Display */}
      <div className="commands">
        {Object.keys(itemsByCategory).length > 0 ? (
          Object.entries(itemsByCategory).map(([categoryName, items]) => (
            <div key={categoryName} className="command-category">
              <div className="command-category-title body-medium">
                {categoryName}
              </div>
              <div className="category-items-grid">
                {items.map((item) => (
                  <PremiumLibraryUnit
                    key={item.id}
                    elements={item.elements}
                    isPending={false}
                    onClick={handleItemClick}
                    svgCache={svgCache}
                    id={item.id}
                    selected={isItemSelected(item.id)}
                    onToggle={handleItemSelectToggle}
                    onDrag={handleItemDrag}
                    item={item}
                    onPremiumPrompt={handlePremiumPrompt}
                  />
                ))}
              </div>
            </div>
          ))
        ) : searchQuery ? (
          <div className="no-match">
            <div className="icon">{searchIcon}</div>
            No items found matching "{searchQuery}"
          </div>
        ) : (
          <div className="no-match">
            <div className="icon">{searchIcon}</div>
            No items available
          </div>
        )}
      </div>

      {/* Purchase Prompt Modal */}
      {showPurchasePrompt && restrictedItem && (
        <PurchasePrompt
          isOpen={showPurchasePrompt}
          onClose={() => {
            setShowPurchasePrompt(false);
            setRestrictedItem(null);
          }}
          item={restrictedItem}
          onPurchaseComplete={() => {
            // Refresh premium status and re-render
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};
